/**
 * Animation constants matching style guide specifications
 */

import type { Variants, Transition } from "framer-motion";

// Duration constants (in seconds for Framer Motion)
export const durations = {
  fast: 0.15, // Button hover, micro-interactions
  base: 0.2, // Modal open, toasts in
  slow: 0.3, // Page transitions
  loading: 1, // Spinner rotation
};

// Easing curves
export const easings = {
  easeOut: [0, 0, 0.2, 1] as const,
  easeIn: [0.4, 0, 1, 1] as const,
  easeInOut: [0.4, 0, 0.2, 1] as const,
};

// Pre-configured transitions
export const springTransition: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 25,
};

export const gentleSpring: Transition = {
  type: "spring",
  stiffness: 300,
  damping: 30,
};

export const bouncySpring: Transition = {
  type: "spring",
  stiffness: 500,
  damping: 15,
};

export const smoothTransition: Transition = {
  duration: durations.base,
  ease: easings.easeOut,
};

export const quickTransition: Transition = {
  duration: durations.fast,
  ease: easings.easeOut,
};

export const pageTransition: Transition = {
  duration: durations.slow,
  ease: easings.easeInOut,
};

// Animation variants for common patterns
export const fadeInUp = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: smoothTransition,
};

export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: durations.base },
};

export const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
  transition: smoothTransition,
};

export const slideInRight = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: pageTransition,
};

export const slideInLeft = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
  transition: pageTransition,
};

// Stagger children animation
export const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

export const staggerItem = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
};

// Legacy variants (for backwards compatibility)
export const buttonVariants: Variants = {
  idle: { scale: 1 },
  hover: { scale: 1.02 },
  tap: { scale: 0.98 },
  disabled: { scale: 1, opacity: 0.5 },
};

export const cardVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: smoothTransition,
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: quickTransition,
  },
};

export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: smoothTransition,
  },
};

export const pageVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  enter: {
    opacity: 1,
    x: 0,
    transition: pageTransition,
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: durations.base,
      ease: easings.easeIn,
    },
  },
};

export const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: smoothTransition,
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: quickTransition,
  },
};

export const slideDownVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: smoothTransition,
  },
};

export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: smoothTransition,
  },
  exit: {
    opacity: 0,
    transition: quickTransition,
  },
};

export const inputVariants: Variants = {
  idle: {
    borderColor: "var(--color-surface-border)",
    boxShadow: "0 0 0 0 transparent",
  },
  focus: {
    borderColor: "var(--color-primary)",
    boxShadow: "var(--shadow-focus)",
  },
  error: {
    borderColor: "var(--color-error)",
    boxShadow: "0 0 0 2px rgba(239, 68, 68, 0.2)",
  },
};

export const labelVariants: Variants = {
  default: {
    y: 0,
    scale: 1,
    color: "var(--color-text-muted)",
  },
  active: {
    y: -24,
    scale: 0.85,
    color: "var(--color-primary)",
  },
  error: {
    y: -24,
    scale: 0.85,
    color: "var(--color-error)",
  },
};

export const checkmarkVariants: Variants = {
  hidden: {
    pathLength: 0,
    opacity: 0,
  },
  visible: {
    pathLength: 1,
    opacity: 1,
    transition: {
      pathLength: { duration: 0.4, ease: "easeOut" },
      opacity: { duration: 0.2 },
    },
  },
};

export const circleVariants: Variants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: bouncySpring,
  },
};

export const shakeVariants: Variants = {
  shake: {
    x: [0, -10, 10, -10, 10, 0],
    transition: { duration: 0.4 },
  },
};

export const cartItemVariants: Variants = {
  hidden: {
    opacity: 0,
    height: 0,
    marginBottom: 0,
  },
  visible: {
    opacity: 1,
    height: "auto",
    marginBottom: 16,
    transition: {
      height: gentleSpring,
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    marginBottom: 0,
    transition: {
      opacity: { duration: 0.15 },
      height: { duration: 0.2, delay: 0.1 },
    },
  },
};

export const progressVariants: Variants = {
  initial: { width: 0 },
  animate: (progress: number) => ({
    width: `${progress}%`,
    transition: {
      duration: 0.5,
      ease: easings.easeInOut,
    },
  }),
};

export const pulseVariants: Variants = {
  pulse: {
    scale: [1, 1.05, 1],
    opacity: [1, 0.8, 1],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const confettiVariants: Variants = {
  initial: {
    y: 0,
    x: 0,
    opacity: 1,
    scale: 1,
    rotate: 0,
  },
  animate: (i: number) => ({
    y: [0, -100, 200],
    x: [0, (i % 2 === 0 ? 1 : -1) * (50 + Math.random() * 100)],
    opacity: [1, 1, 0],
    scale: [1, 1.2, 0.5],
    rotate: [0, 180, 360],
    transition: {
      duration: 1.5 + Math.random() * 0.5,
      ease: "easeOut",
    },
  }),
};

export const tooltipVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 5,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: quickTransition,
  },
};

export const counterTransition: Transition = {
  duration: 0.5,
  ease: easings.easeInOut,
};
